import { BusMessage, MessageFactory, calcObjectByteLength, UID_BYTES_PER_ELEMENT, FLOAT32_BYTES_PER_ELEMENT, BOOLEAN_BYTES_PER_ELEMENT } from "flavr-common";
/*
 * Tells the LocomotionModule to fly to target destination.
 * Will be answered with a DestinationReachedMessage once the destination is reached
 * or with a DestinationChangedMessage if another FlyToDestinationMessage is sent
 * before the destination has been reached.
 */
export class FlyToDestinationMessage extends BusMessage {
    get byteLength() {
        return FlyToDestinationMessage.MAX_BYTE_LENGTH + calcObjectByteLength(this.params);
    }
    constructor(targetCameraWorldPosition = [0, 0, 0], params = {}) {
        super();
        this.targetCameraWorldPosition = targetCameraWorldPosition;
        this.params = params;
    }
    serialization(stream) {
        this.targetCameraWorldPosition = stream.number3(this.targetCameraWorldPosition);
        if (stream.isReading)
            this.params = {};
        this.params.scaleFactor = stream.float32Safe(this.params.scaleFactor);
        this.params.targetScale = stream.float32Safe(this.params.targetScale);
        this.params.velocity = stream.float32Safe(this.params.velocity);
        this.params.durationInSeconds = stream.float32Safe(this.params.durationInSeconds);
        this.params.lookAt = stream.number3Safe(this.params.lookAt);
        this.params.targetAngle = stream.float32Safe(this.params.targetAngle);
    }
    static is(msg) {
        return msg.type === FlyToDestinationMessage.typestatic;
    }
}
FlyToDestinationMessage.typestatic = "FlyToDestinationMessage";
FlyToDestinationMessage.subjectstatic = "locomotion";
FlyToDestinationMessage.MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 3; // targetCameraWorld
MessageFactory.register(FlyToDestinationMessage);
/*
 * Possible answer to FlyToDestinationMessage
 */
export class DestinationReachedMessage extends BusMessage {
    get byteLength() {
        return DestinationReachedMessage.BYTE_LENGTH;
    }
    serialization() { }
    static is(msg) {
        return msg.type === DestinationReachedMessage.typestatic;
    }
}
DestinationReachedMessage.typestatic = "DestinationReachedMessage";
DestinationReachedMessage.subjectstatic = "locomotion";
DestinationReachedMessage.BYTE_LENGTH = 0;
MessageFactory.register(DestinationReachedMessage);
/*
 * Possible answer to FlyToDestinationMessage
 */
export class DestinationChangedMessage extends BusMessage {
    get byteLength() {
        return DestinationChangedMessage.BYTE_LENGTH;
    }
    serialization() { }
    static is(msg) {
        return msg.type === DestinationChangedMessage.typestatic;
    }
}
DestinationChangedMessage.typestatic = "DestinationChangedMessage";
DestinationChangedMessage.subjectstatic = "locomotion";
DestinationChangedMessage.BYTE_LENGTH = 0;
MessageFactory.register(DestinationChangedMessage);
/**
 * Updates the local player platform transform directly.
 */
export class UpdatePlatformTransformMessage extends BusMessage {
    get byteLength() {
        return calcObjectByteLength(this.trans);
    }
    constructor(trans) {
        super();
        this.trans = trans;
    }
    serialization(stream) {
        this.trans = stream.object(this.trans);
    }
    static is(msg) {
        return msg.type === UpdatePlatformTransformMessage.typestatic;
    }
}
UpdatePlatformTransformMessage.typestatic = "UpdatePlatformTransformMessage";
UpdatePlatformTransformMessage.subjectstatic = "locomotion";
MessageFactory.register(UpdatePlatformTransformMessage);
/**
 * Sets the world transform of the player.
 */
export class SetPlayerWorldTransformMessage extends BusMessage {
    get byteLength() {
        return calcObjectByteLength(this.trans);
    }
    constructor(trans) {
        super();
        this.trans = trans;
    }
    serialization(stream) {
        this.trans = stream.object(this.trans);
    }
    static is(msg) {
        return msg.type === SetPlayerWorldTransformMessage.typestatic;
    }
}
SetPlayerWorldTransformMessage.typestatic = "SetPlayerWorldTransformMessage";
SetPlayerWorldTransformMessage.subjectstatic = "locomotion";
MessageFactory.register(SetPlayerWorldTransformMessage);
/** Requests the local player's platform pose for a later teleport back to the same location. */
export class RequestPlayerWorldTransformMessage extends BusMessage {
    get byteLength() { return 0; }
    serialization() { }
    static is(msg) {
        return msg.type === RequestPlayerWorldTransformMessage.typestatic;
    }
}
RequestPlayerWorldTransformMessage.typestatic = "RequestPlayerWorldTransformMessage";
RequestPlayerWorldTransformMessage.subjectstatic = "locomotion";
MessageFactory.register(RequestPlayerWorldTransformMessage);
export class ResponsePlayerWorldTransformMessage extends BusMessage {
    constructor(trans = {}) {
        super();
        this.trans = trans;
    }
    get byteLength() { return calcObjectByteLength(this.trans); }
    serialization(stream) {
        this.trans = stream.object(this.trans);
    }
    static is(msg) {
        return msg.type === ResponsePlayerWorldTransformMessage.typestatic;
    }
}
ResponsePlayerWorldTransformMessage.typestatic = "ResponsePlayerWorldTransformMessage";
ResponsePlayerWorldTransformMessage.subjectstatic = "locomotion";
MessageFactory.register(ResponsePlayerWorldTransformMessage);
/**
 * Enables or disables teleport locomotion.
 */
export class EnableTeleportMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT * 2 + BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(enable, hand) {
        super();
        this.enable = enable;
        this.hand = hand;
    }
    serialization(stream) {
        this.hand = stream.int8Safe(this.hand);
        this.enable = stream.boolean(this.enable);
    }
    static is(msg) {
        return msg.type === EnableTeleportMessage.typestatic;
    }
}
EnableTeleportMessage.typestatic = "EnableTeleportMessage";
EnableTeleportMessage.subjectstatic = "locomotion";
MessageFactory.register(EnableTeleportMessage);
/**
 * Starts a VR teleport action.
 */
export class StartTeleportVrMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(controllerUid) {
        super();
        this.controllerUid = controllerUid;
    }
    serialization(stream) {
        this.controllerUid = stream.uid(this.controllerUid);
    }
    static is(msg) {
        return msg.type === StartTeleportVrMessage.typestatic;
    }
}
StartTeleportVrMessage.typestatic = "StartTeleportVrMessage";
StartTeleportVrMessage.subjectstatic = "locomotion";
MessageFactory.register(StartTeleportVrMessage);
/**
 * Publishes the view direction that should result from a teleport.
 */
export class TeleportResultingViewDirectionMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + FLOAT32_BYTES_PER_ELEMENT;
    }
    constructor(controllerUid, angle) {
        super();
        this.controllerUid = controllerUid;
        this.angle = angle;
    }
    serialization(stream) {
        this.controllerUid = stream.uid(this.controllerUid);
        this.angle = stream.float32Safe(this.angle);
    }
    static is(msg) {
        return msg.type === TeleportResultingViewDirectionMessage.typestatic;
    }
}
TeleportResultingViewDirectionMessage.typestatic = "TeleportResultingViewDirectionMessage";
TeleportResultingViewDirectionMessage.subjectstatic = "locomotion";
MessageFactory.register(TeleportResultingViewDirectionMessage);
